import { Tiptap, useEditorState } from '@tiptap/react'
import classNames from 'classnames'
import clamp from 'lodash/clamp'
import { FC, KeyboardEventHandler, MouseEventHandler } from 'react'
import { useTextEditor } from './context'
import * as styles from './styles'
import { TextEditorContentProps } from './types'

const getIntegerStyle = (style: CSSStyleDeclaration, property: string) =>
  Number.parseInt(style.getPropertyValue(property), 10)

/**
 * Provides the content textarea for the TextEditor. It must be
 * provided as a child to `<TextEditor>`.
 */
export const TextEditorContent: FC<
  React.PropsWithChildren<TextEditorContentProps>
> = ({ children, className, enableSaveShortcut }) => {
  const { editor, isContentOverflowed, onClick, onEscape, saveContent } =
    useTextEditor()
  const { isFocused } = useEditorState({
    editor,
    selector: (snapshot) => ({ isFocused: snapshot.editor.isFocused }),
  })

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === 'Escape' && editor && !editor.isDestroyed) {
      onEscape?.(event)

      // Blur editor if `onEscape` did not call `event.preventDefault()`
      if (editor.isFocused && !event.defaultPrevented) {
        editor.commands.blur()
      }
    }

    if (
      enableSaveShortcut &&
      event.key === 'Enter' &&
      (event.metaKey || event.ctrlKey)
    ) {
      // Delete any trailing hardBreak before saving
      editor
        .chain()
        .focus()
        .command(({ tr, state }) => {
          const lastPos = tr.selection.$head.end()
          const node = state.doc.nodeAt(lastPos - 1)

          if (node?.type.name === 'hardBreak') {
            tr.delete(lastPos - 1, lastPos)
            return true
          }
          return false
        })
        .run()

      saveContent()
    }
  }

  const handleClick: MouseEventHandler = (event) => {
    // Focus editor if content is clicked
    if (editor && !editor.isDestroyed) {
      if (!editor.isFocused) {
        // Try to resolve the closest position to where the user clicked
        let resolvedPosition: ReturnType<typeof editor.view.posAtCoords> = null
        const viewEl =
          event.target instanceof Element && event.target.firstElementChild

        if (viewEl && event.currentTarget === event.target) {
          const viewRect = viewEl.getBoundingClientRect()
          const viewStyle = window.getComputedStyle(viewEl, null)

          // We need to offset padding from the view bounds, so that `posAtCoords`
          // gets the position within the closest node. If the coordinates are in
          // the view's padding, it'll only return the start pos of the closest node
          const coords = {
            left: clamp(
              event.clientX,
              viewRect.left + getIntegerStyle(viewStyle, 'padding-left'),
              viewRect.right - getIntegerStyle(viewStyle, 'padding-right')
            ),
            top: clamp(
              event.clientY,
              viewRect.top + getIntegerStyle(viewStyle, 'padding-top'),
              viewRect.bottom - getIntegerStyle(viewStyle, 'padding-bottom')
            ),
          }

          // Resolve viewport coordinates to a document position
          resolvedPosition = editor.view.posAtCoords(coords)
        }

        // Focus nearest position to where the user clicked
        editor.commands.focus(resolvedPosition?.pos)
      }

      onClick?.(event)
    }
  }

  return (
    <Tiptap.Content
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      css={styles.editorContentWrapper}
      className={classNames('texteditor-content-wrapper', className, {
        'content-overflowed': isContentOverflowed,
        'has-focus': isFocused,
      })}
    >
      {children}
    </Tiptap.Content>
  )
}
